import React from 'react';
import { useHistory, useLocation, matchPath } from 'react-router';
import { useFlags } from 'launchdarkly-react-client-sdk';

// Types
import { OrganizationRoleType } from 'Shared/types/Member.type';

// Constants
import PageUrls from 'Shared/constants/PageUrls.constants';
import { billingStates } from 'Shared/constants/constants';

// Hooks
import { useApp } from 'Shared/utils/hooks/use-app';
import { useOrganizationSubscription } from 'Shared/utils/hooks/use-organization-subscription';
import { useSubscriptionTracking } from 'Shared/tracking/use-subscription-tracking';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

// Components
import { WpButton, WpButtonGroup } from 'Wpds/components/button';
import WpIcon from 'Wpds/components/icon/WpIcon';
import WpBanner from 'Wpds/components/banner/WpBanner';
import WpBackdrop from 'Wpds/components/backdrop/WpBackdrop';

// Styles
import './LimitOfUsersExceededBanner.scss';

function LimitOfUsersExceededBanner() {
  const history = useHistory();
  const { pathname } = useLocation();
  const { intercomContactSalesEventName } = useApp();
  const { trackClickedManageUsersFromUsersExceededBanner } = useSubscriptionTracking();
  const { canSelectBasic, billingState, seatsToBillFor, billingVersion } = useOrganizationSubscription();
  const { featureFlagDisableLimitOfUsersExceededBanner } = useFlags();

  const { user: currentUser } = useCurrentUser();

  const isAdmin = currentUser.organizationRole === OrganizationRoleType.admin;
  const isAdminMembersPage = matchPath(pathname, { path: [PageUrls.ADMIN] });

  const showBanner =
    billingVersion === 2 &&
    seatsToBillFor > 4 &&
    billingState === billingStates.FREE_PLAN &&
    !featureFlagDisableLimitOfUsersExceededBanner;

  const redirectToManageUsers = () => {
    trackClickedManageUsersFromUsersExceededBanner();
    history.push(PageUrls.ADMIN_MEMBERS);
  };

  if (isAdminMembersPage || canSelectBasic || !showBanner) return null;

  const baseClassName = 'LimitOfUsersExceededBanner';

  return (
    <>
      <div className={baseClassName}>
        <WpBanner type="orange" icon={<WpIcon size="lg" name="user-friends" />}>
          <div className="d-flex align-items-center justify-content-between">
            You have exceeded your limit of 4 users. Upgrade now{isAdmin && <> or remove users</>}
            <WpButtonGroup>
              {isAdmin && (
                <WpButton size="sm" color="white" variant="secondary" onClick={redirectToManageUsers}>
                  Manage Users
                </WpButton>
              )}
              <WpButton size="sm" color="white" dataAttributes={{ 'data-intercom': intercomContactSalesEventName }}>
                Contact Sales
              </WpButton>
            </WpButtonGroup>
          </div>
        </WpBanner>
      </div>
      <WpBackdrop />
    </>
  );
}

export default LimitOfUsersExceededBanner;
